import React, { useState, useEffect } from 'react';
import { useTheme } from '../../../core/ui/ThemeContext';
import { useLanguage } from '../../../core/i18n/LanguageContext';
import UserForm from './UserForm';
import PasswordChangeForm from '../PasswordChangeForm';

import OciUserForm from './OciUserForm';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import UserList from './UserList';
import UserManagementHeader from './UserManagementHeader';
import useOciUsers from './useOciUsers';
import { formatDate, filterUsers, deleteUser } from './utils';

const UserManagement = ({ user, adminUsers, onReloadUsers, onUpdateUser, showSuccess, showError, showWarning }) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  
  // State
  const [editingUser, setEditingUser] = useState(null);
  const [editingPassword, setEditingPassword] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const [userType, setUserType] = useState('admin'); // 'admin' oder 'oci'
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState('all');
  const [showPasswords, setShowPasswords] = useState({});

  // OCI Users Hook
  const {
    ociUsers,
    ociLoading,
    loadOciUsers,
    loadOciStatistics,
    createOciUser,
    updateOciUser,
    deleteOciUser,
    reload: reloadOciUsers
  } = useOciUsers(user, showError, showSuccess, t);

  // Effects
  useEffect(() => {
    if (userType === 'oci') {
      loadOciUsers();
      loadOciStatistics();
    }
  }, [userType, loadOciUsers, loadOciStatistics]);

  // Event Handlers
  const handleUserSaved = () => {
    setEditingUser(null);
    if (userType === 'admin' && onReloadUsers) {
      onReloadUsers();
    } else if (userType === 'oci') {
      reloadOciUsers();
    }
  };

  const handlePasswordChanged = () => {
    setEditingPassword(null);
  };

  const handleDeleteUser = async (userId, isOci = false) => {
    try {
      if (isOci) {
        await deleteOciUser(userId);
      } else {
        const success = await deleteUser(userId, false, user, showSuccess, showError, t);
        if (success && onReloadUsers) {
          onReloadUsers();
        }
      }
      setDeleteConfirmation(null);
    } catch (error) {
      showError(error.message);
    }
  };

  const handleCreateOciUser = async (userData) => {
    try {
      if (!userData.username || !userData.password) {
        showWarning(t('admin.users.usernamePasswordRequired'));
        return;
      }

      await createOciUser(userData);
      setEditingUser(null);
    } catch (error) {
      showError(t('admin.userForm.createError') + ': ' + error.message);
    }
  };

  const handleUpdateOciUser = async (userId, userData) => {
    try {
      await updateOciUser(userId, userData);
      setEditingUser(null);
    } catch (error) {
      showError(t('admin.userForm.updateError') + ': ' + error.message);
    }
  };

  const togglePasswordVisibility = (userId) => {
    setShowPasswords(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };

  const handleCreateUser = () => {
    setEditingUser(userType === 'oci' ? {} : {});
  };

  // Filtered users
  const filteredOciUsers = filterUsers(ociUsers, searchTerm, filterActive);
  const filteredAdminUsers = filterUsers(adminUsers, searchTerm);

  // Loading state for admin users
  if (userType === 'admin' && !adminUsers) {
    return (
      <div className="relative group">
        {/* Glass Background */}
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20 backdrop-blur-2xl'
          : 'glass-light-card border border-slate-200'
        } rounded-3xl ${isDarkMode ? 'shadow-[0_8px_32px_rgba(0,0,0,0.4)]' : 'shadow-sm'}`}></div>
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
          : ''
        } rounded-3xl`}></div>

        <div className="relative p-8">
          <UserManagementHeader
            userType={userType}
            setUserType={setUserType}
            searchTerm=""
            setSearchTerm={() => {}}
            filterActive="all"
            setFilterActive={() => {}}
            onCreateUser={() => {}}
          />
          <div className="flex flex-col items-center justify-center py-12">
            <div className={`animate-spin rounded-full h-10 w-10 border-3 mb-4 ${isDarkMode ? 'border-white/20 border-t-white/80' : 'border-slate-200 border-t-brand'}`}></div>
            <p className={`font-medium ${isDarkMode ? 'text-white drop-shadow-lg' : 'text-slate-900'}`}>{t('admin.users.loading')}</p>
            <p className={`mt-1 ${isDarkMode ? 'text-white/60 drop-shadow-sm' : 'text-slate-600'}`}>{t('common.pleaseWait')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header mit Switch und Suchleiste */}
      <UserManagementHeader 
        userType={userType}
        setUserType={setUserType}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        filterActive={filterActive}
        setFilterActive={setFilterActive}
        onCreateUser={handleCreateUser}
      />

      {/* Benutzer erstellen/bearbeiten Form */}
      {editingUser && (
        userType === 'oci' ? (
          <OciUserForm
            userData={editingUser}
            onSave={editingUser.id ? 
              (data) => handleUpdateOciUser(editingUser.id, data) : 
              handleCreateOciUser
            }
            onCancel={() => setEditingUser(null)}
            isEditing={!!editingUser.id}
          />
        ) : (
          <UserForm
            user={user}
            editingUser={editingUser}
            onUserCreated={handleUserSaved}
            onUserUpdated={onUpdateUser}
            onCancel={() => setEditingUser(null)}
            showSuccess={showSuccess}
            showError={showError}
            showWarning={showWarning}
          />
        )
      )}

      {/* Passwort ändern Form (nur für Admin-Benutzer) */}
      {editingPassword && userType === 'admin' && (
        <PasswordChangeForm
          user={user}
          adminUser={editingPassword}
          onPasswordChanged={handlePasswordChanged}
          onCancel={() => setEditingPassword(null)}
          showSuccess={showSuccess}
          showError={showError}
        />
      )}
      
      {/* Benutzer Liste */}
      <div className="relative group">
        {/* Glass Background */}
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-br from-gray-900/30 via-gray-800/20 to-gray-900/30 border border-white/20 backdrop-blur-2xl'
          : 'glass-light-card border border-slate-200'
        } rounded-3xl ${isDarkMode ? 'shadow-[0_8px_32px_rgba(0,0,0,0.4)]' : 'shadow-sm'}`}></div>
        <div className={`absolute inset-0 ${isDarkMode
          ? 'bg-gradient-to-t from-white/5 via-transparent to-white/10'
          : ''
        } rounded-3xl`}></div>
        
        <div className="relative p-6">
          <UserList
            userType={userType}
            users={userType === 'admin' ? filteredAdminUsers : filteredOciUsers}
            loading={userType === 'oci' && ociLoading}
            searchTerm={searchTerm}
            showPasswords={showPasswords}
            onEdit={setEditingUser}
            onChangePassword={setEditingPassword}
            onDelete={setDeleteConfirmation}
            onTogglePasswordVisibility={togglePasswordVisibility}
            formatDate={formatDate}
          />
        </div>
      </div>

      {/* Löschbestätigung Modal */}
      <DeleteConfirmationModal
        deleteConfirmation={deleteConfirmation}
        onConfirm={handleDeleteUser}
        onCancel={() => setDeleteConfirmation(null)}
      />
    </div>
  );
};

export default UserManagement;